import {publicFetch, resolvePublicApiBaseUrl} from '@fitro/customer-host/api'
import {PUBLIC_EVENT_CODE_PATTERN} from '@fitro/event-contract'
import {isIanaTimeZone, parseInstant} from '@fitro/date-time'
import {parseBenefitProvider, type BenefitProvider} from '@fitro/customer-ui/BenefitProvider'
import {isOptionalPublicPassPromotion, type PublicPassPromotion} from './public-passes.service'

export type PublicScheduleInstructor = {
    id: string
    avatarUrl?: string | null
    firstName?: string | null
    lastName?: string | null
}

export type PublicScheduleLocation = {
    id: string
    name?: string | null
}

export type PublicScheduleAvailabilityStatus = 'available' | 'last_spots' | 'full' | 'registration_closed' | 'occurred'

/** A benefit card a guest may declare to pay `amountMinor`; the studio checks the card on site. */
export type PublicGuestBenefitCard = {
    provider: BenefitProvider
    /** Null when the whole brand is accepted; a variant entry exists only when the studio restricts the brand. */
    variant?: string | null
    cardName: string
    /** A separate price for card holders, never a reduction: it carries no Omnibus reference (ADR 0015). */
    amountMinor: number
}

/** A guest's price; `promotionName` is set when a promotion lowered `baseAmountMinor`. */
export type PublicGuestCheckoutPrice = {
    baseAmountMinor: number
    amountMinor: number
    currency: string
    promotionName?: string | null
    /** Set only when a price reduction may be shown: the Omnibus reference to strike through. */
    lowestPrice30dMinor?: number | null
    /** Cards that lower this price when declared, each with its price (always above zero). */
    benefitCards?: PublicGuestBenefitCard[]
}

type PublicBenefitCardPrice = {
    cardName: string
    priceMinor: number
    cards: Array<{provider: string; variant: string | null}>
}

export type PublicScheduleEvent = {
    id: string
    publicCode?: string | null
    availabilityStatus: PublicScheduleAvailabilityStatus
    classColorKey?: string | null
    classCoverImageUrl?: string | null
    className?: string | null
    description?: string | null
    fullDescription?: string | null
    fullDescriptionHtml?: string | null
    eventRules?: string | null
    eventRulesHtml?: string | null
    isCyclical: boolean
    passAccessMode: 'all' | 'selected' | 'none'
    allowedPassTemplateIds: string[]
    priceMinor?: number | null
    /** How the class sells entries: "PAID", "FREE", "PASSES" (only with a pass; no price) or null for a class without a mode. */
    entryMode?: string | null
    /** What a customer with each benefit card pays for the entry (0 = free entry); empty when no card promotion applies. */
    benefitCardPrices?: PublicBenefitCardPrice[]
    priceCurrency?: string | null
    acceptedPaymentMethods?: Array<'cash' | 'online'>
    endDateTime: string
    timeZone: string
    cancellationClosesAt?: string | null
    bookingClosesAt?: string | null
    bookingOpen?: boolean
    guestCheckoutAvailable?: boolean
    /** What a guest pays; present only when guest checkout is available. */
    guestCheckoutPrice?: PublicGuestCheckoutPrice | null
    /** The best automatic promotion for all customers on the paid entry, as the price list shows it; null when none applies. */
    automaticPromotion?: PublicPassPromotion | null
    /** A better price for a customer's first purchase or first class on the paid entry; null when none applies. */
    newCustomerPromotion?: PublicPassPromotion | null
    /** Signing in could give a first visit a first-purchase discount. */
    firstPurchaseDiscountAvailable?: boolean
    waitlistAvailable?: boolean
    /** Free spots, sent only when the studio shows counts for this Term; null otherwise. */
    spotsLeft?: number | null
    instructors: PublicScheduleInstructor[]
    location?: PublicScheduleLocation | null
    startDateTime: string
    status?: 'scheduled' | 'cancelled' | string | null
}

export type PublicSchedulePage = {
    content: PublicScheduleEvent[]
}

export type PublicScheduleQuery = {
    startDate?: string
    endDate?: string
    isCyclical?: boolean
}

export type PublicScheduleRequestErrorKind = 'configuration' | 'http' | 'network' | 'payload'

export class PublicScheduleRequestError extends Error {
    constructor(readonly kind: PublicScheduleRequestErrorKind) {
        super(`Public schedule request failed: ${kind}`)
        this.name = 'PublicScheduleRequestError'
    }
}

const EMPTY_PUBLIC_SCHEDULE_PAGE: PublicSchedulePage = {content: []}

function isPublicSchedulePage(value: unknown): value is PublicSchedulePage {
    if (!value || typeof value !== 'object') return false
    const content = (value as {content?: unknown}).content
    return Array.isArray(content) && content.every(isPublicScheduleEvent)
}

function isNullableString(value: unknown): value is string | null | undefined {
    return value === null || value === undefined || typeof value === 'string'
}

function isNullablePublicEventCode(value: unknown): value is string | null | undefined {
    return value === null || value === undefined || (
        typeof value === 'string' && PUBLIC_EVENT_CODE_PATTERN.test(value)
    )
}

function isPublicScheduleInstructor(value: unknown): value is PublicScheduleInstructor {
    if (!value || typeof value !== 'object') return false
    const instructor = value as PublicScheduleInstructor
    return typeof instructor.id === 'string' && instructor.id.length > 0 && isNullableString(instructor.avatarUrl)
        && isNullableString(instructor.firstName)
        && isNullableString(instructor.lastName)
}

function isPublicScheduleLocation(value: unknown): value is PublicScheduleLocation {
    if (!value || typeof value !== 'object') return false
    const location = value as PublicScheduleLocation
    return typeof location.id === 'string' && location.id.length > 0 && isNullableString(location.name)
}

function isPublicScheduleAvailabilityStatus(value: unknown): value is PublicScheduleAvailabilityStatus {
    return value === 'available'
        || value === 'last_spots'
        || value === 'full'
        || value === 'registration_closed'
        || value === 'occurred'
}

function isValidDateTime(value: unknown): value is string {
    return typeof value === 'string' && parseInstant(value) !== null
}

function isOptionalString(value: unknown): value is string | null | undefined {
    return value === undefined || value === null || typeof value === 'string'
}

function isNullablePriceMinor(value: unknown): value is number | null | undefined {
    return value === null
        || value === undefined
        || (Number.isSafeInteger(value) && (value as number) >= 0)
}

function isBenefitCardPriceArray(value: unknown): value is PublicBenefitCardPrice[] {
    return Array.isArray(value) && value.every(item => item
        && typeof item === 'object'
        && typeof (item as PublicBenefitCardPrice).cardName === 'string'
        && (item as PublicBenefitCardPrice).cardName.trim().length > 0
        && Number.isSafeInteger((item as PublicBenefitCardPrice).priceMinor)
        && (item as PublicBenefitCardPrice).priceMinor >= 0
        && Array.isArray((item as PublicBenefitCardPrice).cards)
        && (item as PublicBenefitCardPrice).cards.every(card => card && typeof card.provider === 'string'
            && (card.variant === null || typeof card.variant === 'string')))
}

function isGuestCheckoutPrice(value: unknown): value is PublicGuestCheckoutPrice {
    if (!value || typeof value !== 'object') return false
    const price = value as PublicGuestCheckoutPrice
    return Number.isSafeInteger(price.baseAmountMinor)
        && Number.isSafeInteger(price.amountMinor)
        && price.amountMinor > 0
        && price.amountMinor <= price.baseAmountMinor
        && typeof price.currency === 'string'
        && isNullableString(price.promotionName)
        && isNullablePriceMinor(price.lowestPrice30dMinor)
        && (price.benefitCards === undefined || isGuestBenefitCardArray(price.benefitCards, price.amountMinor))
}

function isGuestBenefitCardArray(value: unknown, regularAmountMinor: number): value is PublicGuestBenefitCard[] {
    return Array.isArray(value) && value.every(item => Boolean(item)
        && typeof item === 'object'
        && parseBenefitProvider((item as PublicGuestBenefitCard).provider) !== null
        && isOptionalString((item as PublicGuestBenefitCard).variant)
        && typeof (item as PublicGuestBenefitCard).cardName === 'string'
        && (item as PublicGuestBenefitCard).cardName.trim().length > 0
        && Number.isSafeInteger((item as PublicGuestBenefitCard).amountMinor)
        && (item as PublicGuestBenefitCard).amountMinor > 0
        && (item as PublicGuestBenefitCard).amountMinor < regularAmountMinor)
}

function isPublicPassAccessMode(value: unknown): value is PublicScheduleEvent['passAccessMode'] {
    return value === 'all' || value === 'selected' || value === 'none'
}

function isStringArray(value: unknown): value is string[] {
    return Array.isArray(value) && value.every(item => typeof item === 'string' && item.trim().length > 0)
}

function isPaymentMethodArray(value: unknown): value is Array<'cash' | 'online'> {
    return Array.isArray(value) && value.every(item => item === 'cash' || item === 'online')
}

function isPublicScheduleEvent(value: unknown): value is PublicScheduleEvent {
    if (!value || typeof value !== 'object') return false
    const event = value as PublicScheduleEvent
    return typeof event.id === 'string' && Boolean(event.id.trim())
        && isNullablePublicEventCode(event.publicCode)
        && isNullableString(event.classColorKey)
        && isNullableString(event.classCoverImageUrl)
        && isPublicScheduleAvailabilityStatus(event.availabilityStatus)
        && isNullableString(event.className)
        && isNullableString(event.description)
        && isNullableString(event.fullDescription)
        && isNullableString(event.fullDescriptionHtml)
        && isNullableString(event.eventRules)
        && isNullableString(event.eventRulesHtml)
        && typeof event.isCyclical === 'boolean'
        && isPublicPassAccessMode(event.passAccessMode)
        && isStringArray(event.allowedPassTemplateIds)
        && isNullablePriceMinor(event.priceMinor)
        && isNullableString(event.entryMode)
        && (event.benefitCardPrices === undefined || isBenefitCardPriceArray(event.benefitCardPrices))
        && isNullableString(event.priceCurrency)
        && (event.acceptedPaymentMethods === undefined || isPaymentMethodArray(event.acceptedPaymentMethods))
        && isValidDateTime(event.startDateTime)
        && isValidDateTime(event.endDateTime)
        && ((event.cancellationClosesAt === null || event.cancellationClosesAt === undefined) || isValidDateTime(event.cancellationClosesAt))
        && isIanaTimeZone(event.timeZone)
        && (event.bookingClosesAt === null || event.bookingClosesAt === undefined || isValidDateTime(event.bookingClosesAt))
        && (event.bookingOpen === undefined || typeof event.bookingOpen === 'boolean')
        && (event.guestCheckoutAvailable === undefined || typeof event.guestCheckoutAvailable === 'boolean')
        && (event.guestCheckoutPrice === null || event.guestCheckoutPrice === undefined || isGuestCheckoutPrice(event.guestCheckoutPrice))
        && isOptionalPublicPassPromotion(event.automaticPromotion)
        && isOptionalPublicPassPromotion(event.newCustomerPromotion)
        && (event.firstPurchaseDiscountAvailable === undefined || typeof event.firstPurchaseDiscountAvailable === 'boolean')
        && (event.waitlistAvailable === undefined || typeof event.waitlistAvailable === 'boolean')
        && (event.spotsLeft === null || event.spotsLeft === undefined || (Number.isSafeInteger(event.spotsLeft) && event.spotsLeft >= 0))
        && Array.isArray(event.instructors)
        && event.instructors.every(isPublicScheduleInstructor)
        && (event.location === null || event.location === undefined || isPublicScheduleLocation(event.location))
        && isNullableString(event.status)
}

export async function getPublicPartnerEvents(
    slug: string,
    {startDate, endDate, isCyclical}: PublicScheduleQuery
): Promise<PublicSchedulePage> {
    try {
        return await getPublicPartnerEventsStrict(slug, {startDate, endDate, isCyclical})
    } catch {
        return EMPTY_PUBLIC_SCHEDULE_PAGE
    }
}

export async function getPublicPartnerEventsStrict(
    slug: string,
    {startDate, endDate, isCyclical}: PublicScheduleQuery
): Promise<PublicSchedulePage> {
    const normalizedSlug = slug.trim()
    if (!normalizedSlug) throw new PublicScheduleRequestError('configuration')

    const baseUrl = resolvePublicApiBaseUrl()
    if (!baseUrl) throw new PublicScheduleRequestError('configuration')

    const query = new URLSearchParams()
    if (startDate) query.set('startDate', startDate)
    if (endDate) query.set('endDate', endDate)
    if (typeof isCyclical === 'boolean') query.set('isCyclical', String(isCyclical))
    const url = `${baseUrl}/api/public/partners/${encodeURIComponent(normalizedSlug)}/events?${query.toString()}`

    let response: Response
    try {
        response = await publicFetch(url, {
            method: 'GET',
            credentials: 'omit',
            cache: 'no-store',
        })
    } catch {
        throw new PublicScheduleRequestError('network')
    }

    if (!response.ok) throw new PublicScheduleRequestError('http')

    try {
        const body = await response.json()
        if (!isPublicSchedulePage(body)) throw new PublicScheduleRequestError('payload')
        return body
    } catch {
        throw new PublicScheduleRequestError('payload')
    }
}

export async function getPublicPartnerEvent(
    slug: string,
    publicCode: string,
): Promise<PublicScheduleEvent | null> {
    const normalizedSlug = slug.trim()
    const normalizedPublicCode = publicCode.trim()
    if (!normalizedSlug || !PUBLIC_EVENT_CODE_PATTERN.test(normalizedPublicCode)) return null

    const baseUrl = resolvePublicApiBaseUrl()
    if (!baseUrl) return null

    const url = `${baseUrl}/api/public/partners/${encodeURIComponent(normalizedSlug)}/events/${encodeURIComponent(normalizedPublicCode)}`

    try {
        const response = await publicFetch(url, {
            method: 'GET',
            credentials: 'omit',
            cache: 'no-store',
        })
        if (!response.ok) return null

        const body = await response.json()
        return isPublicScheduleEvent(body) && body.publicCode === normalizedPublicCode ? body : null
    } catch {
        return null
    }
}
